import { forwardRef, useImperativeHandle, useRef, useState } from "react";

import CustomTableService from "services/CustomTable";
import CustomTableRowService from "services/CustomTableRow";
import { invalidateCustomTablesQuery } from "hooks/useCustomTablesAdminGrid";
import { formatError } from "routes/custom_tables/helpers";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";

const DeleteTableModal = forwardRef((_props, ref) => {
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Relationship columns elsewhere that point at this table. Deletion is
  // blocked server-side while any exist, so the modal swaps the confirm
  // flow for an explanation listing them.
  const [inboundRefs, setInboundRefs] = useState([]);
  const [rowCount, setRowCount] = useState(null);
  const confirmInputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    open: ({ slug, displayName }) => {
      setSlug(slug);
      setDisplayName(displayName);
      setConfirmText("");
      setError(null);
      setInboundRefs([]);
      setRowCount(null);
      setOpen(true);
      CustomTableService.getInboundRelationships(slug)
        .then(refs => setInboundRefs(Array.isArray(refs) ? refs : []))
        .catch(() => setInboundRefs([]));
      CustomTableRowService.list(slug, { per_page: 1 })
        .then(resp => {
          if (typeof resp?.total === "number") setRowCount(resp.total);
        })
        .catch(() => setRowCount(null));
    },
    close: () => setOpen(false),
  }));

  const handleClose = () => {
    if (loading) return;
    setOpen(false);
  };

  const matches = confirmText === displayName;

  const handleConfirm = async () => {
    if (!matches || !slug) return;
    setLoading(true);
    setError(null);
    try {
      await CustomTableService.delete(slug);
      await invalidateCustomTablesQuery();
      setOpen(false);
    } catch (err) {
      setError(formatError(err, "Failed to delete table"));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const isBlocked = inboundRefs.length > 0;

  if (isBlocked) {
    return (
      <Dialog
        open={open}
        onOpenChange={o => {
          if (!o) handleClose();
        }}
      >
        <DialogContent aria-label="Delete table">
          <DialogTitle>Delete table</DialogTitle>
          <div className="tw:flex tw:flex-col tw:gap-3">
            <p>
              <strong>{displayName}</strong> can&apos;t be deleted yet — other
              tables link to it through relationship fields:
            </p>
            <ul className="tw:list-disc tw:pl-5">
              {inboundRefs.map(refEntry => (
                <li key={`${refEntry.slug}-${refEntry.field_id}`}>
                  <strong>{refEntry.display_name}</strong> —{" "}
                  <code>{refEntry.field_id}</code>
                </li>
              ))}
            </ul>
            <p className="tw:text-muted-foreground">
              Remove those relationship fields first, then delete this table.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        if (!o) handleClose();
      }}
    >
      <DialogContent aria-label="Delete table" initialFocus={confirmInputRef}>
        <DialogTitle>Delete table</DialogTitle>
        <div className="tw:flex tw:flex-col tw:gap-3">
          <p>
            You are about to permanently delete the table{" "}
            <strong>{displayName}</strong>
            {rowCount != null &&
              ` and its ${rowCount} ${rowCount === 1 ? "row" : "rows"}`}
            . All rows and column definitions will be removed. This cannot be
            undone.
          </p>
          <p className="tw:text-muted-foreground">
            To confirm, type the table name <strong>{displayName}</strong>{" "}
            below.
          </p>
          <Field>
            <Input
              ref={confirmInputRef}
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder={displayName}
            />
          </Field>
          {error && (
            <div className="tw:text-sm tw:text-destructive">{error}</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!matches || loading}
          >
            {loading && <Spinner data-icon="inline-start" />}
            Delete table
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

DeleteTableModal.displayName = "DeleteTableModal";

export default DeleteTableModal;
